import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  Button,
  Icon,
  IconButton,
  TooltipContent,
  TooltipRoot,
  TooltipTrigger,
} from '@socialchorus/shared-ui-components';
import { useDuplicateUserGeneratedContentMutation } from '../../../common/queries/use-user-generated-content-query';
import { usePushToastOrFlashMessage } from '../toaster/deprecation-helper';

interface ContentActionDuplicateProps {
  contentId: number;
  isDuplicatable: boolean;
  wideMode?: boolean;
  container?: HTMLElement | null;
}

const ContentActionDuplicate = ({
  contentId,
  isDuplicatable,
  wideMode = false,
  container,
}: ContentActionDuplicateProps) => {
  const { t } = useTranslation(undefined, { useSuspense: false });
  const history = useHistory();
  const { pushMessage } = usePushToastOrFlashMessage();

  const { isWorking, mutate } = useDuplicateUserGeneratedContentMutation(
    contentId,
    {
      onSuccess: (newId: number) => {
        history.push(`/post/${newId}`);
      },
      onError: () => {
        pushMessage({ type: 'error', text: t('errors.default') });
      },
    }
  );

  const actionLabel = t('content_submission.duplicate', 'Duplicate');
  const disabledTooltip = t(
    'content_submission.duplicate_incompatible',
    "This post contains blocks that are incompatible with the editor and can't be duplicated."
  );

  const isDisabled = !isDuplicatable || isWorking;

  const handleClick = () => {
    if (isDisabled) return;
    mutate();
  };

  if (wideMode) {
    return (
      <TooltipRoot>
        <TooltipContent
          description={!isDuplicatable ? disabledTooltip : actionLabel}
          container={container}
        />
        <TooltipTrigger asChild>
          <Button
            onClick={handleClick}
            variant="text"
            size="compact"
            aria-label={actionLabel}
            leftIcon={<Icon>content_copy</Icon>}
            label={actionLabel}
            disabled={isDisabled}
          />
        </TooltipTrigger>
      </TooltipRoot>
    );
  }

  return (
    <TooltipRoot>
      <TooltipContent
        description={!isDuplicatable ? disabledTooltip : actionLabel}
        container={container}
      />
      <TooltipTrigger asChild>
        <IconButton
          aria-label={actionLabel}
          iconName="content_copy"
          onClick={handleClick}
          disabled={isDisabled}
        />
      </TooltipTrigger>
    </TooltipRoot>
  );
};

export default ContentActionDuplicate;
